import { Button, Flex, Modal, UIKitProvider } from "@qws-org/ui-kit";
import type { Emitter } from "mitt";
import type { FC } from "react";
import React, { useEffect, useState } from "react";
import { useOverlayTrigger } from "react-aria";
import { useFetcher, useParams, useRouteLoaderData } from "react-router";
import { useOverlayTriggerState } from "react-stately";

import type { SiteSdk } from "~/core/site-sdk";
import { AuthorizationForm } from "~/features/authorization-form/AuthorizationForm";
import {
  RegistrationForm,
  type RegistrationFormValues,
} from "~/features/registration-form/RegistrationForm";
import { RestorePasswordForm } from "~/features/restore-password-form/RestorePasswordForm";
import { SuccessRegistrationForm } from "~/features/success-registration-form/SuccessRegistrationForm";
import { SuccessRestorePasswordForm } from "~/features/success-restore-password-form/SuccessRestorePasswordForm";
import { Close } from "~/icons";
import type { loader } from "~/root";
import type {
  LoginCredential,
  RestoreCredential,
} from "~/shared/utils/auth-poker-sdk/auth-poker-sdk";
import { createOverrideThemeValues } from "~/shared/utils/create-override-theme-values";
import { createTheme } from "~/shared/utils/create-theme";
import { createLinkComponent } from "~/shared/utils/createLinkComponent";
import { useGtmContext } from "~/shared/utils/gtm-sdk/provider";
import { ModalHandlerBlock } from "~/widgets/auth-modals/components/ModalHandlerBlock";
import { GenerateNicknamePlugin } from "~/widgets/generate-nickname-plugin";
import { OTPRegistrationPlugin } from "~/widgets/otp-registration";
import { PreDepBonusPlugin } from "~/widgets/pre-dep-bonus-plugin";
import { useAction } from "~/widgets/use-action";
import { ValueChainLoginPlugin } from "~/widgets/value-chain-login";
import { ValueChainPlugin } from "~/widgets/value-chain-registration";

import type { AuthModalName, Events } from "./AuthModalsProvider";
import { useOpenModalByQueryParam } from "./useOpenModalByQueryParam";

const DEFAULT_MODAL_STATE: Record<AuthModalName, boolean> = {
  register: false,
  login: false,
  restore: false,
  successRestore: false,
  successRegistration: false,
  fastRegistration: false,
  valueChainRegistration: false,
  otpRegistration: false,
  preDepBonus: false,
  valueChainLogin: false,
};

function isValidationErrorResponse(
  obj: unknown,
): obj is Partial<Record<string, string>> {
  if (typeof obj !== "object" || obj === null || Array.isArray(obj)) {
    return false;
  }

  // Проверяем, что все значения объекта — строки
  return Object.values(obj).every((value) => typeof value === "string");
}

export const Modals: FC<{
  emitter: Emitter<Events>;
  siteSdk: SiteSdk;
  playerId?: string | undefined;
}> = ({ emitter, siteSdk, playerId }) => {
  const data = useRouteLoaderData<typeof loader>("root");
  const authFeature = data?.authFeatures;
  const pageDesignSystem = data?.pageDesignSystem;
  const { lang } = useParams();

  const { callAction: onCallAction, isActionInactive: isControlInactive } =
    useAction();
  const overrideThemeInput = createOverrideThemeValues({
    currentThemeEntry: {
      colors: data?.colors ?? {},
      fontWeight: data?.fontWeight ?? {},
      fontSize: data?.fontSize ?? {},
      fontFamily: data?.fontFamily ?? {},
      lineHeight: data?.lineHeight ?? {},
    },
    newThemeEntry: {
      colors: pageDesignSystem?.colors ?? {},
      fontWeight: pageDesignSystem?.font_weight ?? {},
      fontSize: pageDesignSystem?.font_size ?? {},
      lineHeight: pageDesignSystem?.line_height ?? {},
    },
  });
  const theme = createTheme(lang ?? "", overrideThemeInput);
  const lootBoxSettings = data?.lootBoxSettings;

  const casinoFastRegistrationStandaloneUrl =
    data?.pluginIntegrations?.casinoFastRegistration?.standalone_url;
  const valueChainRegistrationStandaloneUrl =
    data?.pluginIntegrations?.valueChainRegistration?.standalone_url;

  const valueChainLoginStandaloneUrl =
    data?.pluginIntegrations?.valueChainLogin?.standalone_url;
  const preDepBonusStandaloneUrl = `${data?.pluginIntegrations?.preDepBonus?.front_url}/standalone.iife.js`;
  const otpRegistrationStandaloneUrl =
    data?.pluginIntegrations?.otpRegistration?.standalone_url;
  // actions вызываемые при клике на ссылку под формами
  const descriptionLinkControlActionAuth =
    authFeature?.auth?.[0]?.description_link_action;

  const descriptionLinkControlActionRegister =
    authFeature?.registration?.[0]?.description_link_action;

  const isInactiveActionAuth = descriptionLinkControlActionAuth
    ? isControlInactive(descriptionLinkControlActionAuth)
    : true;

  const isInactiveActionRegister = descriptionLinkControlActionRegister
    ? isControlInactive(descriptionLinkControlActionRegister)
    : true;

  const { gtm } = useGtmContext();
  const fetcher = useFetcher();

  const [modalState, setModalState] =
    useState<Record<AuthModalName, boolean>>(DEFAULT_MODAL_STATE);

  const [registrationError, setRegistrationError] = useState<
    Partial<Record<keyof RegistrationFormValues, string>> | undefined | null
  >(null);
  const [loginError, setLoginError] = useState<string | undefined | null>(null);
  const [restoreError, setRestoreError] = useState<string | undefined | null>(
    null,
  );

  const open = async (name: AuthModalName): Promise<void> => {
    if (name === "login") {
      await fetcher.submit(null, {
        method: "PUT",
        action: `/api/auth/login?locale=${lang}`,
      });
    }

    setModalState({ ...DEFAULT_MODAL_STATE, [name]: true });
  };

  const close = (): void => {
    setModalState(DEFAULT_MODAL_STATE);
    setRestoreError(null);
    setLoginError(null);
    setRegistrationError(null);
  };

  const registrationModalState = useOverlayTriggerState({
    isOpen: modalState.register,
    onOpenChange: () => (modalState.register ? close() : open("register")),
  });

  const fastRegistrationModalState = useOverlayTriggerState({
    isOpen: modalState.fastRegistration,
    onOpenChange: () =>
      modalState.fastRegistration ? close() : open("fastRegistration"),
  });
  const valueChainRegistrationModalState = useOverlayTriggerState({
    isOpen: modalState.valueChainRegistration,
    onOpenChange: () =>
      modalState.valueChainRegistration
        ? close()
        : open("valueChainRegistration"),
  });
  const valueChainLoginModalState = useOverlayTriggerState({
    isOpen: modalState.valueChainLogin,
    onOpenChange: () =>
      modalState.valueChainLogin ? close() : open("valueChainLogin"),
  });
  const otpRegistrationModalState = useOverlayTriggerState({
    isOpen: modalState.otpRegistration,
    onOpenChange: () =>
      modalState.otpRegistration ? close() : open("otpRegistration"),
  });

  const registrationOverlay = useOverlayTrigger(
    { type: "dialog" },
    registrationModalState,
  );
  const loginModalState = useOverlayTriggerState({
    isOpen: modalState.login,
    onOpenChange: () => (modalState.login ? close() : open("login")),
  });

  const loginOverlay = useOverlayTrigger({ type: "dialog" }, loginModalState);

  const restoreModalState = useOverlayTriggerState({
    isOpen: modalState.restore,
    onOpenChange: () => (modalState.restore ? close() : open("restore")),
  });

  const restoreOverlay = useOverlayTrigger(
    { type: "dialog" },
    restoreModalState,
  );
  const successRestoreModalState = useOverlayTriggerState({
    isOpen: modalState.successRestore,
    onOpenChange: () =>
      modalState.successRestore ? close() : open("successRestore"),
  });

  const successRestoreOverlay = useOverlayTrigger(
    { type: "dialog" },
    successRestoreModalState,
  );

  const successRegistrationModalState = useOverlayTriggerState({
    isOpen: modalState.successRegistration,
    onOpenChange: () =>
      modalState.successRegistration ? close() : open("successRegistration"),
  });

  const preDepBonusModalState = useOverlayTriggerState({
    isOpen: modalState.preDepBonus,
    onOpenChange: () => {
      if (modalState.preDepBonus) {
        close();
      } else {
        open("preDepBonus");
      }
    },
  });

  const onRegistrationFormSuccess = async (): Promise<void> => {
    gtm.signUp({ eventTime: new Date(), playerID: playerId });
    if (lootBoxSettings?.lootbox) {
      window.location.href = `/${lang}/ftd`;
    } else {
      if (!lang) {
        throw new Error("Lang must be defined");
      }
      const { redirectUrl } = await siteSdk.getRoomUrl(lang);
      window.location.href = redirectUrl;
    }

    close();
  };

  const onRegister = async (
    formData: RegistrationFormValues,
  ): Promise<void> => {
    const body = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      body.append(key, value.toString());
    });
    body.append("locale", lang ?? "");

    try {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        body,
      });

      const responseData = await res.json();

      if (!res.ok) {
        if (isValidationErrorResponse(responseData)) {
          setRegistrationError(responseData);
          return;
        }

        throw new Error(
          `Неизвестная ошибка регистрации: ${JSON.stringify(responseData)}`,
        );
      }

      if (data?.featureFlags.isPreDepBonusEnabled) {
        open("preDepBonus");
      } else {
        open("successRegistration");
        setTimeout(() => {
          onRegistrationFormSuccess();
        }, 4000);
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Ошибка регистрации: ${error.message}`);
      } else {
        throw new Error(`Ошибка регистрации: ${JSON.stringify(error)}`);
      }
    }
  };

  const onLogin = async (formData: LoginCredential): Promise<void> => {
    const body = new FormData();
    body.append("nick", formData.nick);
    body.append("password", formData.password);
    body.append(
      "isRememberPassword",
      formData.isRememberPassword ? "true" : "false",
    );
    body.append("locale", lang ?? "");

    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        body,
      });

      const responseText = await res.text();

      if (!res.ok) {
        if (
          res.status === 400 &&
          responseText === "Invalid authentication data"
        ) {
          setLoginError(responseText);
          return;
        }
        if (res.status === 500) {
          setLoginError(responseText);
          return;
        }

        throw new Error(`Неизвестная ошибка: ${responseText}`);
      }

      if (typeof lang === "undefined") {
        throw new Error("Lang must be defined");
      }
      gtm.logIn({ eventTime: new Date(), playerID: playerId });
      const { redirectUrl } = await siteSdk.getRoomUrl(lang);

      window.location.href = redirectUrl;

      close();
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? `Ошибка входа: ${error.message}`
          : `Ошибка входа: ${JSON.stringify(error)}`,
      );
    }
  };

  const onRestore = async (formData: RestoreCredential): Promise<void> => {
    const body = new FormData();
    body.append("email", formData.email);
    body.append("locale", lang ?? "");

    try {
      const res = await fetch("/api/auth/restore-password", {
        method: "POST",
        body,
      });

      const responseData = await res.json();

      if (!res.ok) {
        const { error } = responseData;

        if (typeof error === "string" && error === "email is incorrect") {
          setRestoreError(error);
          return;
        }

        throw new Error(
          `Неизвестная ошибка восстановления: ${JSON.stringify(responseData)}`,
        );
      }

      open("successRestore");
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? `Ошибка восстановления: ${error.message}`
          : `Ошибка восстановления: ${JSON.stringify(error)}`,
      );
    }
  };

  useEffect(() => {
    emitter.on("open", open);
    emitter.on("close", close);

    return () => {
      emitter.off("open", open);
      emitter.off("close", close);
    };
  }, []);

  useOpenModalByQueryParam(open);

  return (
    <>
      {registrationModalState.isOpen && (
        <Modal
          state={registrationModalState}
          isDismissable
          closeButtonSlot={(onClose) => (
            <Button
              onPress={onClose}
              autoFocus
              variant="transparent"
              border={{ width: "0" }}
              position={{
                type: "absolute",
                top: 14,
                right: 16,
              }}
              md={{ position: { top: 36, right: 40 } }}
              data-testId="registration-modal-close-button"
            >
              <Close viewBox="0 0 32 32" className="w-6 h-6 md:w-8 md:h-8" />
            </Button>
          )}
        >
          <UIKitProvider
            value={{
              components: {
                Link: createLinkComponent(lang ?? ""),
              },
              theme,
            }}
          >
            <Flex align="center" flexDirection="column" p={{ px: 8 }}>
              {React.cloneElement(
                <RegistrationForm
                  serverErrors={registrationError}
                  data={{
                    validation_invalid_password:
                      authFeature?.registration?.[0]
                        ?.validation_invalid_password ?? "",
                    validation_nickname_contain_password:
                      authFeature?.registration?.[0]
                        ?.validation_nickname_contain_password ?? "",
                    form_title:
                      authFeature?.registration?.[0]?.form_title ?? "",
                    form_description_text:
                      authFeature?.registration?.[0]?.form_description_text ??
                      "",
                    form_email_input_label:
                      authFeature?.registration?.[0]?.form_email_input_label ??
                      "",
                    form_nickname_input_label:
                      authFeature?.registration?.[0]
                        ?.form_nickname_input_label ?? "",
                    form_password_input_label:
                      authFeature?.registration?.[0]
                        ?.form_password_input_label ?? "",
                    form_promo_code_button_text:
                      authFeature?.registration?.[0]
                        ?.form_promo_code_button_text ?? "",
                    form_promo_code_input_label:
                      authFeature?.registration?.[0]
                        ?.form_promo_code_input_label ?? "",
                    form_remember_checkbox_text:
                      authFeature?.registration?.[0]
                        ?.form_remember_checkbox_text ?? "",
                    form_submit_button_text:
                      authFeature?.registration?.[0]?.form_submit_button_text ??
                      "",
                    validation_invalid_email:
                      authFeature?.registration?.[0]
                        ?.validation_invalid_email ?? "",
                    validation_required:
                      authFeature?.registration?.[0]?.validation_required ?? "",
                    validation_nickname_max_length:
                      authFeature?.registration?.[0]
                        ?.validation_nickname_max_length ?? "",
                    validation_nickname_min_length:
                      authFeature?.registration?.[0]
                        ?.validation_nickname_min_length ?? "",
                    validation_password_min_width:
                      authFeature?.registration?.[0]
                        ?.validation_password_min_width ?? "",
                    validation_password_max_width:
                      authFeature?.registration?.[0]
                        ?.validation_password_max_width ?? "",
                    validation_nickname_contains_only_numbers:
                      authFeature?.registration?.[0]
                        ?.validation_nickname_contains_only_numbers ?? "",
                    description_link_text:
                      authFeature?.registration?.[0]?.description_link_text ??
                      "",
                    description_text:
                      authFeature?.registration?.[0]?.description_text ?? "",
                    validation_email_max_length:
                      authFeature?.registration?.[0]
                        ?.validation_email_max_length ?? "",
                    validation_email_min_length:
                      authFeature?.registration?.[0]
                        ?.validation_email_min_length ?? "",
                  }}
                  onSubmit={onRegister}
                />,
                registrationOverlay.overlayProps,
              )}

              <ModalHandlerBlock
                descriptionText={
                  authFeature?.registration?.[0]?.description_text ?? ""
                }
                descriptionLinkText={
                  authFeature?.registration?.[0]?.description_link_text ?? ""
                }
                handleClick={() => {
                  // переключение при клике на ссылку под формой регистрации
                  // если экшен не задан в цмс, делаем старое флоу
                  isInactiveActionRegister &&
                  descriptionLinkControlActionRegister
                    ? onCallAction(descriptionLinkControlActionRegister)
                    : open("login");
                }}
              />
            </Flex>
          </UIKitProvider>
        </Modal>
      )}
      {fastRegistrationModalState.isOpen && (
        <Modal
          state={fastRegistrationModalState}
          isDismissable
          closeButtonSlot={(onClose) => (
            <Button
              onPress={onClose}
              autoFocus
              variant="transparent"
              border={{ width: "0" }}
              position={{
                type: "absolute",
                top: 14,
                right: 16,
              }}
              md={{ position: { top: 36, right: 40 } }}
              data-testId="fast-registration-modal-close-button"
            >
              <Close viewBox="0 0 32 32" className="w-6 h-6 md:w-8 md:h-8" />
            </Button>
          )}
        >
          <UIKitProvider
            value={{
              components: {
                Link: createLinkComponent(lang ?? ""),
              },
              theme,
            }}
          >
            <Flex align="center" flexDirection="column" p={{ px: 8 }}>
              {casinoFastRegistrationStandaloneUrl && (
                <GenerateNicknamePlugin />
              )}

              <ModalHandlerBlock
                descriptionText={
                  authFeature?.registration?.[0]?.description_text ?? ""
                }
                descriptionLinkText={
                  authFeature?.registration?.[0]?.description_link_text ?? ""
                }
                handleClick={() => open("login")}
              />
            </Flex>
          </UIKitProvider>
        </Modal>
      )}
      {valueChainRegistrationModalState.isOpen && (
        <Modal state={valueChainRegistrationModalState} p={0}>
          <Flex
            align="center"
            flexDirection="column"
            height={"100dvh"}
            md={{ height: "auto" }}
          >
            {valueChainRegistrationStandaloneUrl && <ValueChainPlugin />}
          </Flex>
        </Modal>
      )}
      {valueChainLoginModalState.isOpen && (
        <Modal state={valueChainLoginModalState} p={0}>
          <Flex
            align="center"
            flexDirection="column"
            height={"100dvh"}
            md={{ height: "auto" }}
          >
            {valueChainLoginStandaloneUrl && <ValueChainLoginPlugin />}
          </Flex>
        </Modal>
      )}
      {otpRegistrationModalState.isOpen && (
        <Modal state={otpRegistrationModalState} p={0}>
          <Flex align="center" flexDirection="column" md={{ height: "auto" }}>
            {otpRegistrationStandaloneUrl && <OTPRegistrationPlugin />}
          </Flex>
        </Modal>
      )}
      {loginModalState.isOpen && (
        <Modal
          state={loginModalState}
          isDismissable
          closeButtonSlot={(onClose) => (
            <Button
              onPress={onClose}
              autoFocus
              variant="transparent"
              border={{ width: "0" }}
              position={{
                type: "absolute",
                top: 14,
                right: 16,
              }}
              md={{ position: { top: 36, right: 40 } }}
              data-testId="login-modal-close-button"
            >
              <Close viewBox="0 0 32 32" className="w-6 h-6 md:w-8 md:h-8" />
            </Button>
          )}
        >
          <UIKitProvider
            value={{
              components: {
                Link: createLinkComponent(lang ?? ""),
              },
              theme,
            }}
          >
            <Flex align="center" flexDirection="column" p={{ px: 8 }}>
              {React.cloneElement(
                <AuthorizationForm
                  serverError={loginError}
                  data={{
                    id: "login",
                    description_link_text:
                      authFeature?.auth?.[0]?.description_link_text ?? "",
                    description_text:
                      authFeature?.auth?.[0]?.description_text ?? "",
                    form_input_label_name:
                      authFeature?.auth?.[0]?.form_input_label_name ?? "",
                    form_input_label_password:
                      authFeature?.auth?.[0]?.form_input_label_password ?? "",
                    form_remember_checkbox_text:
                      authFeature?.auth?.[0]?.form_remember_checkbox_text ?? "",
                    form_restore_link_text: authFeature?.auth?.[0]
                      ?.form_restore_enabled
                      ? (authFeature?.auth?.[0]?.form_restore_link_text ?? "")
                      : " ",
                    form_submit_text:
                      authFeature?.auth?.[0]?.form_submit_text ?? "",
                    form_title: authFeature?.auth?.[0]?.form_title ?? "",
                    validation_invalid_credentials:
                      authFeature?.auth?.[0]?.validation_invalid_credentials ??
                      "",
                    validation_required:
                      authFeature?.auth?.[0]?.validation_required ?? "",
                  }}
                  onSubmit={onLogin}
                  onForgotPassword={() =>
                    authFeature?.auth?.[0]?.form_restore_enabled &&
                    open("restore")
                  }
                />,
                loginOverlay.overlayProps,
              )}
              <ModalHandlerBlock
                descriptionText={authFeature?.auth?.[0]?.description_text ?? ""}
                descriptionLinkText={
                  authFeature?.auth?.[0]?.description_link_text ?? ""
                }
                // переключение при клике на ссылку под формой логина
                // если экшен не задан в цмс, делаем старое флоу
                handleClick={() => {
                  !isInactiveActionAuth && descriptionLinkControlActionAuth
                    ? onCallAction(descriptionLinkControlActionAuth)
                    : open("register");
                }}
              />
            </Flex>
          </UIKitProvider>
        </Modal>
      )}
      {restoreModalState.isOpen && (
        <Modal
          state={restoreModalState}
          isDismissable
          closeButtonSlot={(onClose) => (
            <Button
              onPress={onClose}
              autoFocus
              variant="transparent"
              border={{ width: "0" }}
              position={{
                type: "absolute",
                top: 14,
                right: 16,
              }}
              md={{ position: { top: 36, right: 40 } }}
              data-testId="restore-password-modal-close-button"
            >
              <Close viewBox="0 0 32 32" className="w-6 h-6 md:w-8 md:h-8" />
            </Button>
          )}
        >
          <UIKitProvider
            value={{
              components: {
                Link: createLinkComponent(lang ?? ""),
              },
              theme,
            }}
          >
            <Flex align="center" flexDirection="column" p={{ px: 8 }}>
              {React.cloneElement(
                <RestorePasswordForm
                  serverResponse={restoreError}
                  data={{
                    id: "restore-password",
                    description_link_text:
                      authFeature?.restore_password?.[0]
                        ?.description_link_text ?? "",
                    description_text:
                      authFeature?.restore_password?.[0]?.description_text ??
                      "",
                    form_email_input_label:
                      authFeature?.restore_password?.[0]
                        ?.form_email_input_label ?? "",
                    validation_required:
                      authFeature?.restore_password?.[0]?.validation_required ??
                      "",
                    form_submit_button_text:
                      authFeature?.restore_password?.[0]
                        ?.form_submit_button_text ?? "",
                    form_title:
                      authFeature?.restore_password?.[0]?.form_title ?? "",
                    form_description:
                      authFeature?.restore_password?.[0]?.form_description ??
                      "",
                    validation_invalid:
                      authFeature?.restore_password?.[0]?.validation_invalid ??
                      "",
                  }}
                  onSubmit={onRestore}
                />,
                restoreOverlay.overlayProps,
              )}

              <ModalHandlerBlock
                descriptionText={authFeature?.auth?.[0]?.description_text ?? ""}
                descriptionLinkText={
                  authFeature?.auth?.[0]?.description_link_text ?? ""
                }
                handleClick={() => open("register")}
              />
            </Flex>
          </UIKitProvider>
        </Modal>
      )}
      {successRestoreModalState.isOpen && (
        <Modal state={successRestoreModalState} isDismissable>
          <UIKitProvider
            value={{
              components: {
                Link: createLinkComponent(lang ?? ""),
              },
              theme,
            }}
          >
            <Flex align="center" flexDirection="column" p={{ px: 8 }}>
              {React.cloneElement(
                <SuccessRestorePasswordForm
                  data={{
                    id: "restore-password",
                    description_link_text:
                      authFeature?.restore_password?.[0]
                        ?.description_link_text ?? "",
                    description_text:
                      authFeature?.restore_password?.[0]?.description_text ??
                      "",
                    form_email_input_label:
                      authFeature?.restore_password?.[0]
                        ?.form_email_input_label ?? "",
                    validation_required:
                      authFeature?.restore_password?.[0]?.validation_required ??
                      "",
                    form_submit_button_text:
                      authFeature?.restore_password?.[0]
                        ?.form_submit_button_text ?? "",
                    form_title:
                      authFeature?.restore_password?.[0]?.form_title ?? "",
                    form_description:
                      authFeature?.restore_password?.[0]?.form_description ??
                      "",
                    validation_invalid:
                      authFeature?.restore_password?.[0]?.validation_invalid ??
                      "",
                  }}
                  onSubmit={close}
                />,
                successRestoreOverlay.overlayProps,
              )}

              <ModalHandlerBlock
                descriptionText={authFeature?.auth?.[0]?.description_text ?? ""}
                descriptionLinkText={
                  authFeature?.auth?.[0]?.description_link_text ?? ""
                }
                handleClick={() => open("register")}
              />
            </Flex>
          </UIKitProvider>
        </Modal>
      )}
      {successRegistrationModalState.isOpen && (
        <Modal state={successRegistrationModalState} isDismissable>
          <UIKitProvider
            value={{
              components: {
                Link: createLinkComponent(lang ?? ""),
              },
              theme,
            }}
          >
            <Flex align="center" flexDirection="column" p={{ px: 8 }}>
              <SuccessRegistrationForm
                data={{
                  success_title:
                    authFeature?.registration?.[0]?.success_title ?? "",
                  success_message:
                    authFeature?.registration?.[0]?.success_message ?? "",
                  redirect_message:
                    authFeature?.registration?.[0]?.redirect_message ?? "",
                  redirect_hint:
                    authFeature?.registration?.[0]?.redirect_hint ?? "",
                  open_app_button:
                    authFeature?.registration?.[0]?.open_app_button ?? "",
                }}
                onComplete={onRegistrationFormSuccess}
              />

              <ModalHandlerBlock
                descriptionText={
                  authFeature?.registration?.[0]?.description_text ?? ""
                }
                descriptionLinkText={
                  authFeature?.registration?.[0]?.description_link_text ?? ""
                }
                handleClick={() => open("login")}
              />
            </Flex>
          </UIKitProvider>
        </Modal>
      )}
      {preDepBonusModalState.isOpen && (
        <Modal state={preDepBonusModalState} isDismissable>
          <UIKitProvider
            value={{
              components: {
                Link: createLinkComponent(lang ?? ""),
              },
              theme,
            }}
          >
            <Flex align="center" flexDirection="column">
              {preDepBonusStandaloneUrl && (
                <PreDepBonusPlugin closeModal={close} />
              )}
            </Flex>
          </UIKitProvider>
        </Modal>
      )}
    </>
  );
};
